"use client";

import { parseISO } from "date-fns-jalali";
import { useTranslations } from "next-intl";
import { BlurImage, SellerSharePrice, WalletAddress } from "@/components/core";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useDateFormatter } from "@/lib/i18n/date";
import { formatNumber } from "@/lib/utils/number-utils";
import type { components } from "@/types/api";

type OrderWithSeller = components["schemas"]["UserOrderItemDto"];
type OrderWithSimplifiedSeller = components["schemas"]["UserChildOrderItemDto"];
type PurchasedOrder = components["schemas"]["UserPurchasedOrderItemDto"];
type AcceptableOrder =
  | OrderWithSeller
  | OrderWithSimplifiedSeller
  | PurchasedOrder;

interface PaidPriceData {
  seller_share_unit?: string | number | null;
  seller_share_currency?: string | null;
  seller_settlement_currency?: string | null;
  bv?: string | number | null;
}

// paid_price is typed as Record<string, never> by OpenAPI codegen but at runtime contains price fields
function extractPaidPrice(value: unknown): PaidPriceData | null {
  if (!value || typeof value !== "object") return null;
  const obj = Object.fromEntries(Object.entries(value));
  return {
    seller_share_unit: obj.seller_share_unit ?? null,
    seller_share_currency:
      typeof obj.seller_share_currency === "string"
        ? obj.seller_share_currency
        : null,
    seller_settlement_currency:
      typeof obj.seller_settlement_currency === "string"
        ? obj.seller_settlement_currency
        : null,
    bv: obj.bv ?? null,
  };
}

const getPaidPrice = (order: AcceptableOrder): PaidPriceData | null => {
  if ("paid_price" in order) return extractPaidPrice(order.paid_price);
  return null;
};

const getBvValue = (
  order: AcceptableOrder,
  paidPrice: PaidPriceData | null,
): number => {
  if (paidPrice?.bv != null) {
    const val =
      typeof paidPrice.bv === "string"
        ? parseFloat(paidPrice.bv)
        : paidPrice.bv;
    if (!Number.isNaN(val)) return val;
  }
  return order.bv_value ?? 0;
};

const getProductImage = (order: AcceptableOrder): string => {
  if ("product" in order && order.product) {
    if ("product_images" in order.product) {
      return (
        order.product.product_images[0]?.url ||
        "/images/status/defult-purchase.png"
      );
    }
    if ("image" in order.product && order.product.image) {
      return order.product.image.url || "/images/status/defult-purchase.png";
    }
  }
  return "/images/status/defult-purchase.png";
};

export const UserOrdersProduct = ({
  order,
  user,
}: {
  order: AcceptableOrder;
  user?: components["schemas"]["SimplifiedSellerDto"] | null;
}) => {
  const t = useTranslations("orders");
  const tc = useTranslations("common");
  const df = useDateFormatter();
  const paidPrice = getPaidPrice(order);
  const bv = getBvValue(order, paidPrice);

  return (
    <div className="p-3 bg-bg-secondary space-y-4 rounded-md w-full">
      <div className="flex items-center gap-x-2 text-text-primary">
        <BlurImage
          src={getProductImage(order)}
          alt={order.product?.title || "order image"}
          width={42}
          height={42}
          loading="lazy"
          wrapperClassName="size-10.5 rounded-xs border border-white"
          className="rounded-xs object-cover size-10.5"
        />
        <div className="text-xs text-right space-y-1">
          <p>{order.product?.title || "-"}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs">{df.numeric(parseISO(order.created_at))}</p>
        <Badge
          variant={"secondary"}
          className="rounded-full p-0 py-0.5 px-1 text-[10px] font-inter"
        >
          {formatNumber(bv)}BV
        </Badge>
      </div>
      <Collapsible>
        <CollapsibleContent className="flex flex-col gap-y-4 text-xs">
          <div className="flex items-center w-full justify-between">
            {paidPrice ? (
              <SellerSharePrice
                seller_share_unit={paidPrice.seller_share_unit}
                seller_share_currency={paidPrice.seller_share_currency}
                seller_settlement_currency={
                  paidPrice.seller_settlement_currency
                }
                bv={paidPrice.bv}
                className="text-xs"
              />
            ) : (
              <p className="font-inter">{formatNumber(order.sv_value)}UVM</p>
            )}
            <p>{t("item.sellerShare")}</p>
          </div>
          {user && (
            <div className="flex flex-row-reverse items-center w-full justify-between">
              <p>{user.nickname || "-"}</p>
              <WalletAddress
                className="flex-row-reverse gap-x-1"
                address={user.wallet_address}
              />
            </div>
          )}
          <div className="flex items-center w-full justify-between">
            <p className="font-inter">{order._order_id}</p>
            <p>{t("item.orderId")}</p>
          </div>
        </CollapsibleContent>
        <CollapsibleTrigger
          className="w-full text-xs data-[state=open]:pt-4 flex items-center justify-center gap-x-2"
          openedText={tc("viewLess")}
          closedText={tc("viewMore")}
        />
      </Collapsible>
    </div>
  );
};
